import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import { useSnackbar } from 'notistack';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ButtonSlim from '../../../../../../web-app-components/components/ButtonSlim';
import ButtonFilePicker from '../../../../../../web-app-components/components/ButtonFilePicker';
import FilePreview from '../../../../../../web-app-components/components/FilePreview';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import Grid from '@material-ui/core/Grid';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import CircularProgress from '@material-ui/core/CircularProgress';

import objectsClient from '../../../../../../clients/objects';
import subcategoriesClient from '../../../../../../clients/subcategories';
import {
  parseGPX,
  parseGPXFromText,
  formatCoordinates,
} from '../../../../utils/parseGPX';

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  accordionDetails: {
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      maxHeight: 'calc(100vh - 250px)',
    },
  },
  coordinateItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  poiItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.selected,
  },
  coordinateInfo: {
    marginBottom: theme.spacing(1),
  },
  poiCheckbox: {
    marginBottom: theme.spacing(1),
  },
  nameField: {
    marginBottom: theme.spacing(1),
  },
  audioSection: {
    marginTop: theme.spacing(1),
  },
  audioPreview: {
    marginTop: theme.spacing(1),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  audioPlayer: {
    flex: 1,
    minWidth: 200,
  },
  audioActions: {
    flexShrink: 0,
  },
  objectSelectField: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 200,
  },
  searchField: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

const CoordinateAudioGuidesField = ({ name, gpxFile, disabled, locale, ...props }) => {
  const classes = useStyles();
  const [{ value }, { error, touched }, { setValue }] = useField(name);
  const { enqueueSnackbar } = useSnackbar();
  const [coordinates, setCoordinates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [objectsList, setObjectsList] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [subcategoriesList, setSubcategoriesList] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [objectSearchValue, setObjectSearchValue] = useState('');
  const [objectSelectOpenCoord, setObjectSelectOpenCoord] = useState(null);
  const effectiveLocale = locale || 'lt-LT';

  const filteredObjects = useMemo(() => {
    if (!objectSearchValue) return objectsList;
    const searchLower = objectSearchValue.toLowerCase();
    return objectsList.filter((obj) =>
      (obj.Title || '').toLowerCase().includes(searchLower),
    );
  }, [objectsList, objectSearchValue]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setObjectsLoading(true);
        const data = await objectsClient.find({
          sort: 'Title:asc',
          locale: effectiveLocale,
        });
        if (!cancelled) setObjectsList(data || []);
      } catch (err) {
        if (!cancelled) {
          enqueueSnackbar('Nepavyko gauti objektų sąrašo.', {
            variant: 'error',
          });
          setObjectsList([]);
        }
      } finally {
        if (!cancelled) setObjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, effectiveLocale]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setSubcategoriesLoading(true);
        const data = await subcategoriesClient.find({
          sort: 'Order:asc',
          locale: effectiveLocale,
        });
        if (!cancelled) setSubcategoriesList(data || []);
      } catch (err) {
        if (!cancelled) {
          enqueueSnackbar('Nepavyko gauti subkategorijų sąrašo.', {
            variant: 'error',
          });
          setSubcategoriesList([]);
        }
      } finally {
        if (!cancelled) setSubcategoriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, effectiveLocale]);

  // Parse GPX file when it changes
  useEffect(() => {
    let cancelled = false;

    if (gpxFile && gpxFile instanceof File) {
      setLoading(true);
      parseGPX(gpxFile)
        .then((parsedCoords) => {
          if (cancelled) return;
          setCoordinates(parsedCoords);

          // Keep only guides whose coordinates exist in the newly parsed GPX
          const coordKeys = new Set(
            parsedCoords.map((c) => formatCoordinates(c.lat, c.lng)),
          );
          const existingGuides = value || [];
          const filteredGuides = existingGuides.filter((guide) =>
            guide.Coordinates ? coordKeys.has(guide.Coordinates) : false,
          );
          setValue(filteredGuides);
        })
        .catch((error) => {
          if (cancelled) return;
          enqueueSnackbar(`Nepavyko apdoroti GPX failo: ${error.message}`, {
            variant: 'error',
          });
          setCoordinates([]);
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    } else if (!gpxFile || !gpxFile.id) {
      setCoordinates([]);
      if (!value || value.length === 0) {
        setValue([]);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [gpxFile, setValue, enqueueSnackbar]);

  // When GPX file is already uploaded (has id and url), fetch it and parse to show all POIs
  useEffect(() => {
    let cancelled = false;
    const url = gpxFile?.url;
    const hasUploadedFile =
      gpxFile && gpxFile.id && url && !(gpxFile instanceof File);

    if (!hasUploadedFile) {
      return;
    }

    setLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const parsedCoords = parseGPXFromText(text);
        setCoordinates(parsedCoords);

        // Keep only guides whose coordinates exist in the parsed GPX
        const coordKeys = new Set(
          parsedCoords.map((c) => formatCoordinates(c.lat, c.lng)),
        );
        const existingGuides = value || [];
        const filteredGuides = existingGuides.filter((guide) =>
          guide.Coordinates ? coordKeys.has(guide.Coordinates) : false,
        );
        if (filteredGuides.length !== existingGuides.length) {
          setValue(filteredGuides);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        enqueueSnackbar(`Nepavyko gauti GPX failo: ${err.message}`, {
          variant: 'error',
        });
        setCoordinates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gpxFile?.id, gpxFile?.url, setValue, enqueueSnackbar]);

  const handlePOIToggle = (coordIndex) => {
    const coord = coordinates[coordIndex];
    if (!coord) return;

    const coordString = formatCoordinates(coord.lat, coord.lng);
    const currentValue = value || [];
    const existingIndex = currentValue.findIndex(
      (guide) => guide.Coordinates === coordString,
    );

    if (existingIndex >= 0) {
      // Remove POI
      const newValue = currentValue.filter((_, idx) => idx !== existingIndex);
      setValue(newValue);
    } else {
      // Add POI
      const newValue = [
        ...currentValue,
        {
          Coordinates: coordString,
          AudioFile: null,
          Name: coord.name || '',
          object: null,
          subcategory: null,
        },
      ];
      setValue(newValue);
    }
  };

  const getFirstSubcategoryIdForObject = (objectId) => {
    if (!objectId) return null;
    const obj = objectsList.find((o) => o.id === objectId);
    const subs = obj?.subcategories;
    if (!Array.isArray(subs) || subs.length === 0) return null;
    return subs[0]?.id ?? null;
  };

  const handleObjectChange = (coordString, objectId) => {
    const currentValue = value || [];
    const existingIndex = currentValue.findIndex(
      (guide) => guide.Coordinates === coordString,
    );
    if (existingIndex < 0) return;

    const newValue = [...currentValue];
    const resolvedObjectId = objectId || null;
    const coordIdx = coordinates.findIndex(
      (c) => formatCoordinates(c.lat, c.lng) === coordString,
    );
    const pointOrder1Based = coordIdx >= 0 ? coordIdx + 1 : null;

    let nextName = newValue[existingIndex].Name;
    if (resolvedObjectId != null) {
      const obj = objectsList.find(
        (o) => String(o.id) === String(resolvedObjectId),
      );
      const objectTitle = (obj?.Title || '').trim();
      if (objectTitle) {
        nextName =
          pointOrder1Based != null
            ? `${pointOrder1Based} ${objectTitle}`
            : objectTitle;
      }
    }

    newValue[existingIndex] = {
      ...newValue[existingIndex],
      object: resolvedObjectId,
      Name: nextName,
      ...(resolvedObjectId != null
        ? { subcategory: getFirstSubcategoryIdForObject(resolvedObjectId) }
        : {}),
    };
    setValue(newValue);
  };

  const handleSubcategoryChange = (coordString, subcategoryId) => {
    const currentValue = value || [];
    const existingIndex = currentValue.findIndex(
      (guide) => guide.Coordinates === coordString,
    );
    if (existingIndex >= 0) {
      const newValue = [...currentValue];
      newValue[existingIndex] = {
        ...newValue[existingIndex],
        subcategory: subcategoryId || null,
      };
      setValue(newValue);
    }
  };

  const handleNameChange = (coordString, newName) => {
    const currentValue = value || [];
    const existingIndex = currentValue.findIndex(
      (guide) => guide.Coordinates === coordString,
    );

    if (existingIndex >= 0) {
      const newValue = [...currentValue];
      newValue[existingIndex] = {
        ...newValue[existingIndex],
        Name: newName,
      };
      setValue(newValue);
    }
  };

  const handleAudioSelect = (coordString, files) => {
    if (files && files.length > 0) {
      const currentValue = value || [];
      const existingIndex = currentValue.findIndex(
        (guide) => guide.Coordinates === coordString,
      );

      if (existingIndex >= 0) {
        const newValue = [...currentValue];
        newValue[existingIndex] = {
          ...newValue[existingIndex],
          AudioFile: files[0],
        };
        setValue(newValue);
      }
    }
  };

  const handleAudioRemove = (coordString) => {
    const currentValue = value || [];
    const existingIndex = currentValue.findIndex(
      (guide) => guide.Coordinates === coordString,
    );

    if (existingIndex >= 0) {
      const newValue = [...currentValue];
      newValue[existingIndex] = {
        ...newValue[existingIndex],
        AudioFile: null,
      };
      setValue(newValue);
    }
  };

  const isPOI = (coord) => {
    const coordString = formatCoordinates(coord.lat, coord.lng);
    return (value || []).some((guide) => guide.Coordinates === coordString);
  };

  const getPOIData = (coord) => {
    const coordString = formatCoordinates(coord.lat, coord.lng);
    return (value || []).find((guide) => guide.Coordinates === coordString);
  };

  if (loading) {
    return (
      <div className={classes.container}>
        <Typography>Apdorojamas GPX failas...</Typography>
      </div>
    );
  }

  if (!gpxFile) {
    return (
      <div className={classes.container}>
        <Typography variant="body2" color="textSecondary">
          Įkelkite GPX failą, kad galėtumėte pridėti garso gidus lankytinoms
          vietoms
        </Typography>
      </div>
    );
  }

  // If GPX file is already uploaded but we have no URL to fetch (or fetch not yet done)
  if (gpxFile.id && !gpxFile.url && coordinates.length === 0) {
    return (
      <div className={classes.container}>
        <Typography variant="body2" color="textSecondary">
          GPX failas įkeltas, bet jo turinio nepavyko gauti. Įkelkite GPX failą
          iš naujo.
        </Typography>
      </div>
    );
  }

  if (coordinates.length === 0) {
    return (
      <div className={classes.container}>
        <Typography variant="body2" color="textSecondary">
          GPX faile nerasta koordinačių su pavadinimu
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Typography variant="h6" className={classes.header}>
        Audio gido koordinatės ir failai:
      </Typography>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {coordinates.length} koordinatė(čių) rasta GPX faile
            {value &&
              value.length > 0 &&
              ` • ${value.length} pažymėta kaip lankytinos vietos`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Grid container spacing={2}>
            {coordinates.map((coord, index) => {
              const coordString = formatCoordinates(coord.lat, coord.lng);
              const isPointOfInterest = isPOI(coord);
              const poiData = isPointOfInterest ? getPOIData(coord) : null;
              const audioFile = poiData?.AudioFile;

              return (
                <Grid item xs={12} key={index}>
                  <Paper
                    className={
                      isPointOfInterest
                        ? classes.poiItem
                        : classes.coordinateItem
                    }
                    elevation={isPointOfInterest ? 2 : 1}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isPointOfInterest}
                          onChange={() => handlePOIToggle(index)}
                          disabled={disabled}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="subtitle2">
                          Pažymėti kaip lankytiną vietą (POI)
                        </Typography>
                      }
                      className={classes.poiCheckbox}
                    />

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.coordinateInfo}
                    >
                      Koordinatė {index + 1}: {coordString}
                      {coord.name && ` (${coord.name})`}
                    </Typography>

                    {isPointOfInterest && (
                      <>
                        <TextField
                          className={classes.nameField}
                          label="Pavadinimas (neprivaloma)"
                          value={poiData?.Name || ''}
                          onChange={(e) =>
                            handleNameChange(coordString, e.target.value)
                          }
                          disabled={disabled}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />

                        <FormControl
                          variant="outlined"
                          size="small"
                          fullWidth
                          className={classes.objectSelectField}
                          disabled={disabled}
                          InputLabelProps={{ shrink: true }}
                        >
                          <InputLabel>Objektas</InputLabel>
                          <Select
                            open={objectSelectOpenCoord === coordString}
                            onOpen={() => {
                              setObjectSelectOpenCoord(coordString);
                              setObjectSearchValue('');
                            }}
                            onClose={() => {
                              setObjectSelectOpenCoord(null);
                              setObjectSearchValue('');
                            }}
                            value={poiData?.object?.id ?? poiData?.object ?? ''}
                            onChange={(e) =>
                              handleObjectChange(
                                coordString,
                                e.target.value || null,
                              )
                            }
                            label="Objektas"
                            displayEmpty
                            renderValue={(id) => {
                              const obj = objectsList.find((o) => o.id === id);
                              return obj ? obj.Title : id;
                            }}
                            MenuProps={{
                              PaperProps: { style: { maxHeight: 400 } },
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                            }}
                          >
                            <div
                              style={{
                                padding: 12,
                                position: 'sticky',
                                top: 0,
                                background: '#fff',
                                zIndex: 100,
                              }}
                            >
                              <TextField
                                placeholder="Ieškoti..."
                                value={objectSearchValue}
                                onChange={(e) =>
                                  setObjectSearchValue(e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon />
                                    </InputAdornment>
                                  ),
                                }}
                                variant="outlined"
                                size="small"
                                className={classes.searchField}
                              />
                            </div>
                            {objectsLoading ? (
                              <MenuItem disabled>
                                <CircularProgress size={24} />
                              </MenuItem>
                            ) : (
                              filteredObjects.map((obj) => (
                                <MenuItem key={obj.id} value={obj.id}>
                                  {obj.Title}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>

                        <FormControl
                          variant="outlined"
                          size="small"
                          fullWidth
                          className={classes.objectSelectField}
                          disabled={disabled}
                          InputLabelProps={{ shrink: true }}
                        >
                          <InputLabel>Subkategorija (neprivaloma)</InputLabel>
                          <Select
                            value={
                              poiData?.subcategory?.id ??
                              poiData?.subcategory ??
                              ''
                            }
                            onChange={(e) =>
                              handleSubcategoryChange(
                                coordString,
                                e.target.value || null,
                              )
                            }
                            label="Subkategorija (neprivaloma)"
                            displayEmpty
                            renderValue={(id) => {
                              if (id === '' || id == null) return '—';
                              const sub = subcategoriesList.find((s) => s.id === id);
                              return sub ? sub.Title : id;
                            }}
                          >
                            <MenuItem value="">
                              <em>—</em>
                            </MenuItem>
                            {subcategoriesLoading ? (
                              <MenuItem disabled>
                                <CircularProgress size={24} />
                              </MenuItem>
                            ) : (
                              subcategoriesList.map((sub) => (
                                <MenuItem key={sub.id} value={sub.id}>
                                  {sub.Title}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>

                        <div className={classes.audioSection}>
                          <Typography variant="body2" gutterBottom>
                            Garso gidas:
                          </Typography>
                          {!audioFile ? (
                            <ButtonFilePicker
                              accept="audio/*"
                              onSelect={(files) =>
                                handleAudioSelect(coordString, files)
                              }
                              disabled={disabled}
                              {...props}
                            >
                              Pridėti garso gidą
                            </ButtonFilePicker>
                          ) : (
                            <div className={classes.audioPreview}>
                              <div className={classes.audioPlayer}>
                                <FilePreview
                                  file={audioFile}
                                  disabled={disabled}
                                />
                              </div>
                              <div className={classes.audioActions}>
                                <ButtonSlim
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleAudioRemove(coordString)}
                                  disabled={disabled}
                                >
                                  Pašalinti
                                </ButtonSlim>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
      {touched && error && <FormHelperText error>{error}</FormHelperText>}
    </div>
  );
};

CoordinateAudioGuidesField.defaultProps = {
  disabled: false,
  locale: 'lt-LT',
};

CoordinateAudioGuidesField.propTypes = {
  name: PropTypes.string.isRequired,
  gpxFile: PropTypes.oneOfType([
    PropTypes.instanceOf(File),
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string,
    }),
  ]),
  disabled: PropTypes.bool,
  locale: PropTypes.string,
};

export default CoordinateAudioGuidesField;
